'use client';

import { memo } from 'react';
import { BellIcon, MapIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { Anchor } from '@components/common';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';
import { cn } from '@utils/cn';
import type { JobSearchQueryParams } from '@type/filters';
import {
  McCardType,
  MC_CARD_TYPE_ALERT,
} from './cardTypes';

interface CompactJobAlertCardProps {
  alert?: ApiSchema<'JobSearchAlertData'>;
  title?: string;
  type?: McCardType;
  isSelected?: boolean;
  onSelect?: (alert?: ApiSchema<'JobSearchAlertData'>) => void;
  onEdit?: () => void;
  editHref?: string;
  /**
   * Callback for the MAP action button. Receives query params derived from the card's data.
   * Provided by a parent outside the import cycle (e.g. MissionControl).
   */
  onOpenMap?: (queryParams?: Partial<JobSearchQueryParams>) => void;
  locale: Locale;
  /** Overrides the computed href. Useful when the card should link outside the MC context (e.g. guest users). */
  href?: string;
  /** Location string for non-alert cards (e.g. last experience). Alert cards derive location from their model. */
  location?: string;
}

const ICON_BTN_CLASS = 'flex size-8 items-center justify-center text-ink transition-colors hover:text-primary';

/**
 * @description Compact candidate sidebar view — used for job alerts, last experience, and next-job suggestions.
 * Shows the title and three icon action buttons: MAP, EDIT, NEW.
 */
const CompactJobAlertCard = (props: CompactJobAlertCardProps) => {
  const {
    alert, title, type = MC_CARD_TYPE_ALERT, isSelected, onSelect, onEdit, editHref, onOpenMap, locale,
    href: hrefOverride, location: locationProp,
  } = props;
  const { t } = useTranslation('common');

  // Build card title from alert filters or use the provided custom title
  const getAlertTitle = () => {
    if (title) return title;
    if (!alert) return t('job-alerts.default-title') || 'Job Alert';

    const { jobSearch } = alert;
    if (!jobSearch) return t('job-alerts.default-title') || 'Job Alert';

    if (jobSearch.keyword) return jobSearch.keyword;
    if (jobSearch.domain && jobSearch.domain.length > 0) return jobSearch.domain[0].name || t('job-alerts.default-title');
    if (jobSearch.location && jobSearch.location.length > 0) return jobSearch.location[0];
    return t('job-alerts.default-title') || 'Job Alert';
  };

  const cardTitle = getAlertTitle();

  const href = hrefOverride ?? (() => {
    const keyword = type === MC_CARD_TYPE_ALERT ? alert?.jobSearch?.keyword : title;
    const loc = type === MC_CARD_TYPE_ALERT
      ? alert?.jobSearch?.location?.[0]
      : locationProp;

    if (loc) {
      const locationSlug = slugify(loc);
      const keywordSlug = keyword ? `/${slugify(keyword)}` : '';
      return getI18nLinkHref(`/mc/job/list/location-${locationSlug}${keywordSlug}`, locale);
    }
    if (keyword) {
      return getI18nLinkHref(`/mc/job/list/${slugify(keyword)}`, locale);
    }
    return getI18nLinkHref('/mc', locale);
  })();

  const mapLocation = type === MC_CARD_TYPE_ALERT ? alert?.jobSearch?.location?.[0] : locationProp;

  const handleOpenMap = () => {
    const keyword = type === MC_CARD_TYPE_ALERT ? alert?.jobSearch?.keyword : title;
    onOpenMap?.({
      ...(keyword && { keyword }),
      ...(mapLocation && { location: [mapLocation] }),
    });
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg py-2 transition-colors',
        isSelected && 'bg-primary/5',
      )}
    >
      {/* Title — shrinks to fill remaining space, truncates on overflow */}
      <Anchor
        href={href}
        styling="none"
        className="min-w-0 flex-1"
        onClick={(e) => {
          if (onSelect) {
            e.preventDefault();
            onSelect(alert);
          }
        }}
      >
        <span className={cn(
          'block truncate text-sm font-semibold text-ink hover:text-primary',
          isSelected && 'text-primary',
        )}
        >
          {cardTitle}
        </span>
      </Anchor>

      {/* Icon action buttons: MAP · EDIT · NEW */}
      {onOpenMap && (
        <button
          type="button"
          aria-label={mapLocation ?? t('add.job.open.map')}
          title={mapLocation ?? t('add.job.open.map')}
          className={ICON_BTN_CLASS}
          onClick={handleOpenMap}
        >
          <MapIcon className="size-5" />
        </button>
      )}

      {onEdit && (
        <Anchor
          href={editHref ?? '#'}
          styling="none"
          aria-label={t('my-jobs.card.actions.edit')}
          title={t('my-jobs.card.actions.edit')}
          className={ICON_BTN_CLASS}
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
        >
          <PencilIcon className="size-5" />
        </Anchor>
      )}

      {/* Bell icon — same action as clicking the title */}
      <Anchor
        href={href}
        styling="none"
        aria-label={t('header.label.new.job') || 'New'}
        title={t('header.label.new.job') || 'New'}
        className={ICON_BTN_CLASS}
        onClick={(e) => {
          if (onSelect) {
            e.preventDefault();
            onSelect(alert);
          }
        }}
      >
        <BellIcon className="size-5" />
      </Anchor>
    </div>
  );
};

export const MemoizedCompactJobAlertCard = memo(CompactJobAlertCard);

